import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

// Maps URL param → API policy_type value
const POLICY_TYPE_MAP = {
  privacy:  'Privacy Policy',
  terms:    'Terms and Condition',
  return:   'Return Policy',
  shipping: 'Orders and shipping',  // ✅ Backend value is "Orders and shipping"
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const PolicyPage = () => {
  const { policyType } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPolicy = async () => {
      setLoading(true);
      setError(null);

      const apiPolicyType = POLICY_TYPE_MAP[policyType];
      if (!apiPolicyType) {
        setError('Unknown policy type');
        setLoading(false);
        return;
      }

      try {
        const res = await fetch('https://evercorenutrition.com/ev_admin/api/policies/');
        if (!res.ok) throw new Error('Failed to fetch policies');

        const data = await res.json();

        // Handle paginated response (results array) or direct array
        let policiesArray = [];
        if (data.results && Array.isArray(data.results)) {
          policiesArray = data.results;
        } else if (Array.isArray(data)) {
          policiesArray = data;
        }

        // Log available policies for debugging
        // console.log('Available policies:', policiesArray.map(p => p.policy_type));
        // console.log('Looking for:', apiPolicyType);

        // Match by policy_type field (exact string from API)
        const found = policiesArray.find(
          (p) => p.policy_type === apiPolicyType
        );

        if (found) {
          setPolicy(found);
        } else {
          const availableTypes = policiesArray.map(p => p.policy_type).join(', ');
          setError(`Policy "${apiPolicyType}" not found. Available: ${availableTypes}`);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicy();
  }, [policyType]);

  const getDisplayName = () => {
    if (policy?.policy_type_display) return policy.policy_type_display;
    
    switch(policyType) {
      case 'privacy': return 'Privacy Policy';
      case 'terms': return 'Terms and Conditions';
      case 'return': return 'Return Policy';
      case 'shipping': return 'Orders and Shipping Policy';
      default: return policyType ? policyType.charAt(0).toUpperCase() + policyType.slice(1) + ' Policy' : 'Policy';
    }
  };

  const displayName = getDisplayName();

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading policy...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <h2 className="text-danger mb-4">Policy Not Found</h2>
          <p className="mb-4">{error}</p>
          <div style={{ marginTop: '20px' }}>
            <button className="btn btn-primary me-2" onClick={() => navigate('/')}>
              Go to Home
            </button>
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!policy) return null;

  return (
    <>
      <Navbar />

      {/* Breadcrumb */}
      <section className="breadcrumb-section pt-0">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-contain">
                <h2>{displayName}</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <a href="/"><i className="fa-solid fa-house"></i></a>
                    </li>
                    <li className="breadcrumb-item active">{displayName}</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Policy Header */}
      <section className="policy-header-section">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="policy-header-card">
                <div className="policy-header-content">
                  <h1 className="policy-title">{displayName}</h1>
                  <div className="policy-meta">
                    <div className="last-updated">
                      <i className="fa-regular fa-calendar"></i>
                      <span>Last Updated: <strong>{formatDate(policy.updated_at)}</strong></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Policy Content */}
      <section className="policy-section section-b-space">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="policy-card">
                <div
                  className="policy-content"
                  dangerouslySetInnerHTML={{ __html: policy.description }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .policy-section { padding: 40px 0; }
        .policy-header-section { padding: 20px 0 0; }

        .policy-header-card {
          background: linear-gradient(135deg, #3b3b3b 0%, #000 100%);
          border-radius: 16px;
          padding: 40px;
          margin-bottom: 30px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.15);
        }
        .policy-header-content { color: #fff; }
        .policy-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 20px;
          line-height: 1.2;
        }
        .policy-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 30px;
          background: rgba(255,255,255,0.15);
          padding: 15px 25px;
          border-radius: 50px;
          backdrop-filter: blur(10px);
        }
        .policy-meta > div {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 1rem;
        }
        .policy-meta i { font-size: 1.2rem; opacity: 0.9; }
        .policy-meta strong { font-weight: 600; color: #fff; }

        .policy-card {
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          padding: 40px;
        }

        .policy-content {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #333;
          line-height: 1.7;
        }

        .policy-content h1 {
          font-size: 2.2rem; font-weight: 600; color: #1a1a1a;
          margin: 0 0 1.5rem; padding-bottom: 0.5rem;
          border-bottom: 2px solid #f0f0f0;
        }
        .policy-content h2 {
          font-size: 1.8rem; font-weight: 600; color: #2d2d2d;
          margin: 2rem 0 1rem;
        }
        .policy-content h3 {
          font-size: 1.4rem; font-weight: 500; color: #3d3d3d;
          margin: 1.5rem 0 0.8rem;
        }
        .policy-content h4 {
          font-size: 1.2rem; font-weight: 500; color: #4d4d4d;
          margin: 1.2rem 0 0.5rem;
        }

        .policy-content p {
          margin-bottom: 1rem;
          font-size: 1rem;
          color: #555;
        }
        .policy-content strong { color: #2d2d2d; font-weight: 600; }

        .policy-content ul { list-style-type: disc; margin-bottom: 1.5rem; padding-left: 2rem; }
        .policy-content ol { list-style-type: decimal; margin-bottom: 1.5rem; padding-left: 2rem; }
        .policy-content li { margin-bottom: 0.5rem; color: #555; display: list-item; }
        .policy-content li::marker { color: #3f3f41; font-size: 1rem; }

        .policy-content .ql-indent-1 { padding-left: 2rem; }
        .policy-content .ql-indent-2 { padding-left: 4rem; }
        .policy-content .ql-indent-3 { padding-left: 6rem; }

        .policy-content a { color: #000; text-decoration: none; transition: color .3s; }
        .policy-content a:hover { color: #333; text-decoration: underline; }

        @media (max-width: 768px) {
          .policy-header-card { padding: 30px 20px; }
          .policy-title { font-size: 2rem; }
          .policy-meta { flex-direction: column; gap: 15px; padding: 20px; border-radius: 20px; }
          .policy-card { padding: 25px 20px; }
          .policy-content h1 { font-size: 1.8rem; }
          .policy-content h2 { font-size: 1.5rem; }
          .policy-content h3 { font-size: 1.25rem; }
          .policy-content ul, .policy-content ol { padding-left: 1.5rem; }
        }

        @media (max-width: 480px) {
          .policy-title { font-size: 1.8rem; }
          .policy-card { padding: 20px 15px; }
          .policy-content h1 { font-size: 1.5rem; }
          .policy-content h2 { font-size: 1.3rem; }
          .policy-content h3 { font-size: 1.1rem; }
          .policy-meta { font-size: 0.9rem; }
        }
      `}</style>

      <Footer />
    </>
  );
};

export default PolicyPage;